<!-- 分析规则 -->
<template>
  <div v-loading="showLoading" class="rule">
    <div class="rule__card">
      <div class="rule__card-title">
        <span> 分析规则 </span>
      </div>
      <!-- 自由规则 / 格式化规则 切换 -->
      <el-radio-group v-model="isLiberalRules" style="margin-bottom: 12px">
        <el-radio :label="1">自由规则</el-radio>
        <el-radio :label="0">格式化规则</el-radio>
      </el-radio-group>
      <!-- 自由规则内容区 -->
      <template v-if="isLiberalRules === 1">
        <div class="is-free rule__card-rule">
          <div v-show="polishLoading" class="rule__content-skeleton">
            <div class="rule__content-skeleton-inner">
              <el-skeleton animated>
                <template #template>
                  <div class="rule__content-skeleton-rows">
                    <el-skeleton-item class="rule__content-skeleton-row" variant="text" />
                    <el-skeleton-item class="rule__content-skeleton-row" variant="text" />
                    <el-skeleton-item
                      class="rule__content-skeleton-row rule__content-skeleton-row--short"
                      variant="text"
                    />
                    <el-skeleton-item
                      class="rule__content-skeleton-row rule__content-skeleton-row--medium"
                      variant="text"
                    />
                  </div>
                </template>
              </el-skeleton>
            </div>
          </div>
          <el-input
            v-show="!polishLoading"
            v-model="liberalRule"
            :maxlength="2000"
            :minlength="10"
            placeholder="请输入内容"
            :rows="17"
            show-word-limit
            type="textarea"
            @input="checkChangeAiPrompt"
          />
          <div class="rule__card-rule-polish">
            <AiPolish
              ref="aiPolishRef"
              :disabled="!liberalRule.trim() || polishLoading"
              :params="polishParams"
              @end="handlePolishEnd"
              @start="handlePolishStart"
              @success="handlePolishSuccess"
              @withdraw="handleWithdrawPolish"
            />
          </div>
        </div>
      </template>
      <!-- 格式化规则内容区 -->
      <template v-else>
        <el-form
          ref="formRef"
          :label-width="0"
          :model="formModel"
          :rules="formRules"
          :validate-on-rule-change="false"
        >
          <div v-show="!isFoldUp" class="rule__card-rule">
            <div
              v-for="(summary, summaryIndex) in ruleConfig"
              :key="summaryIndex"
              class="rule__card-rule-item"
            >
              <div class="rule__card-rule-item-title">
                <span>分析模块</span>
                <div class="rule__card-rule-item-title-operate">
                  <i
                    v-if="summaryIndex > 0"
                    class="icon-arrow-up-line t-iconfont"
                    @click.stop="moveUpAnalysisModule(summaryIndex)"
                  ></i>
                  <i
                    v-if="summaryIndex < ruleConfig?.length - 1"
                    class="icon-arrow-down-line t-iconfont"
                    @click.stop="moveDownAnalysisModule(summaryIndex)"
                  ></i>
                  <i
                    class="icon-delete-bin-line t-iconfont"
                    @click.stop="openDelCommitDialog(summaryIndex)"
                  ></i>
                </div>
              </div>
              <div class="rule__card-rule-item-level1">
                <i
                  class="icon-arrow-down-s-line t-iconfont"
                  :style="{
                    transform: summary.isFoldUp ? 'rotate(180deg)' : ''
                  }"
                  @click="changeFoldUpStatus(summaryIndex)"
                ></i>
                <el-form-item :prop="`ruleConfig[${summaryIndex}].itemName`">
                  <el-input
                    v-model="summary.itemName"
                    :maxlength="20"
                    placeholder='标题，例："需求匹配程度"'
                    show-word-limit
                  />
                </el-form-item>
              </div>
              <el-form-item
                v-show="!summary.isFoldUp"
                class="has-textarea"
                :prop="`ruleConfig[${summaryIndex}].itemValue`"
              >
                <el-input
                  v-show="!summary.isFoldUp"
                  v-model="summary.itemValue"
                  :maxlength="300"
                  placeholder="描述需要分析的核心内容保证 AI 助手分析的质量"
                  :rows="3"
                  show-word-limit
                  style="margin-left: 24px"
                  type="textarea"
                />
              </el-form-item>
            </div>
            <div v-if="ruleConfig?.length > 0" class="rule__card-rule-divider"></div>
            <div
              class="rule__card-rule-add"
              :style="{
                color: ruleConfig?.length >= 15 ? '#C8C9CC' : '#7a52f4'
              }"
              @click="addAnalysisModule"
            >
              + 添加（{{ ruleConfig?.length }}/15）
            </div>
          </div>
        </el-form>
      </template>
    </div>
  </div>
  <!-- 删除二次确认弹窗 -->
  <Dialog v-model:show="dialogVisible" align-center title="删除确认" width="300px">
    <span>删除后不可恢复，请确认</span>
    <template #footer>
      <div class="dialog-footer">
        <el-button color @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="summaryRemoveItem">删除</el-button>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick, computed } from 'vue'
import Dialog from '@/components/ai-dialog/index.vue'
import AiPolish from '@/components/ai-polish/index.vue'
import { getQualityAnalysisRule, updateQualityAnalysisRule } from '@/api'
import { ElMessage } from 'element-plus'

const emits = defineEmits(['afterSave'])

// 加载中状态显示标志
const showLoading = ref(false)
// 删除确认弹窗显示标志
const dialogVisible = ref(false)
// 当前删除的分析模块索引
const currentDelRuleIndex = ref(null)
// 分析规则是否收起
const isFoldUp = ref(false)
// 分析规则
const ruleConfig = ref([
  {
    itemName: '',
    itemValue: ''
  }
])
// 分析规则id
const ruleId = ref('')
const formRef = ref(null)
// formModel.ruleConfig 指向 ruleConfig.value，保证表单模型与数据同步
const formModel = reactive({ ruleConfig: ruleConfig.value })
// 动态校验规则对象
const formRules = reactive({})

const isLiberalRules = ref(0) // 0: 格式化规则 1: 自由规则
const liberalRule = ref('')

// AI 润色相关
const aiPolishRef = ref(null)
const polishLoading = ref(false)
const lastOriginalLiberalRule = ref('')

const polishParams = computed(() => ({
  sceneType: 2,
  userPrompt: liberalRule.value
}))

const handlePolishSuccess = (result) => {
  if (!result) return
  lastOriginalLiberalRule.value = liberalRule.value
  liberalRule.value = result
}

const handlePolishStart = () => {
  polishLoading.value = true
}

const handlePolishEnd = () => {
  polishLoading.value = false
}

const handleWithdrawPolish = () => {
  if (!lastOriginalLiberalRule.value) return
  liberalRule.value = lastOriginalLiberalRule.value
  lastOriginalLiberalRule.value = ''
}

const checkChangeAiPrompt = (value) => {
  if (aiPolishRef.value?.success && lastOriginalLiberalRule.value !== value) {
    aiPolishRef.value.reset()
  }
}

// 根据当前 ruleConfig 动态构建 rules
const buildFormRules = () => {
  // 清空已有规则
  Object.keys(formRules).forEach((k) => {
    delete formRules[k]
  })

  const list = ruleConfig.value || []
  list.forEach((item, idx) => {
    // 标题必填
    formRules[`ruleConfig[${idx}].itemName`] = [
      { required: true, message: `请填写模块标题`, trigger: 'blur' }
    ]
    formRules[`ruleConfig[${idx}].itemValue`] = [
      { required: true, message: `请填写模块描述`, trigger: 'blur' }
    ]
  })
}
// 保证 formModel.ruleConfig 与 ruleConfig.value 始终同步，ruleConfig 变化时重建 rules
watch(
  ruleConfig,
  (newVal) => {
    formModel.ruleConfig = newVal
    nextTick(() => {
      buildFormRules()
    })
  },
  { deep: true, immediate: true }
)

/**
 * 获取质量分析规则
 */
const getTheQualityAnalysisRule = async () => {
  showLoading.value = true
  try {
    const { result } = await getQualityAnalysisRule({})
    ruleId.value = result?.data?.id || ''
    ruleConfig.value = result?.data?.rule?.summary || []
    liberalRule.value = result?.data?.liberalRule || ''
    isLiberalRules.value = result?.data?.isLiberalRules || 0
  } catch (error) {
    console.error('获取质量分析规则失败', error)
    throw error
  } finally {
    showLoading.value = false
  }
}
/**
 * 更新质量分析规则
 */
const updateTheQualityAnalysisRule = async () => {
  // 公共请求参数
  const params = {
    id: ruleId.value,
    isLiberalRules: isLiberalRules.value,
    liberalRule: liberalRule.value,
    rule: { summary: ruleConfig.value }
  }

  // 自由规则模式：校验 liberalRule 非空后直接提交
  if (isLiberalRules.value === 1) {
    if (!liberalRule.value.trim()) {
      ElMessage.error('请输入自由规则内容')
      return
    }
    try {
      await updateQualityAnalysisRule(params)
      ElMessage.success('分析规则更新成功')
      emits('afterSave')
    } catch (error) {
      console.error('更新质量分析规则失败', error)
    }
    return
  }

  // 格式化规则模式：走表单校验
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) return
  try {
    await updateQualityAnalysisRule(params)
    ElMessage.success('分析规则更新成功')
    emits('afterSave')
  } catch (error) {
    console.error('更新质量分析规则失败', error)
  }
}
/**
 * 新增分析模块
 */
const addAnalysisModule = () => {
  if (ruleConfig.value?.length >= 15) return
  // 新增空项
  ruleConfig.value.push({
    itemName: '',
    itemValue: ''
  })
}
/**
 * 删除分析模块
 */
const summaryRemoveItem = () => {
  const rule = ruleConfig.value || []
  if (rule.length < 2) {
    ElMessage.error('至少保留一个分析规则')
    return
  }
  ruleConfig.value?.splice(currentDelRuleIndex.value, 1)
  dialogVisible.value = false
}
/**
 * 切换分析模块展开收起状态
 * @param index 分析模块索引
 */
const changeFoldUpStatus = (index) => {
  const rule = ruleConfig.value[index]
  if (rule) {
    rule.isFoldUp = !rule.isFoldUp
  }
}
/**
 * 分析模块上移
 * @param index 分析模块索引
 */
const moveUpAnalysisModule = (index) => {
  if (index > 0) {
    const rule = ruleConfig.value || []
    rule.splice(index - 1, 0, rule.splice(index, 1)[0])
  }
}
/**
 * 分析模块下移
 * @param index 分析模块索引
 */
const moveDownAnalysisModule = (index) => {
  if (index < ruleConfig.value?.length - 1) {
    const rule = ruleConfig.value || []
    rule.splice(index + 1, 0, rule.splice(index, 1)[0])
  }
}
/**
 * 打开删除确认弹窗
 * @param index 分析模块索引
 */
const openDelCommitDialog = (index) => {
  currentDelRuleIndex.value = index
  dialogVisible.value = true
}

onMounted(() => {
  getTheQualityAnalysisRule()
})

defineExpose({
  updateTheQualityAnalysisRule
})
</script>

<style lang="scss" scoped>
.has-textarea {
  :deep(.el-form-item__content > .el-form-item__error) {
    margin-left: 24px !important;
  }
}
.rule__content-skeleton {
  width: 100%;
  height: 367px;
}
.rule__content-skeleton-inner {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 8px 11px;
  overflow: hidden;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 0 1px var(--el-input-border-color, #dcdfe6) inset;
}
.rule__content-skeleton-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rule__content-skeleton-row {
  width: 100%;
  height: 20px;
  border-radius: 8px;
  &--short {
    width: 60%;
  }
  &--medium {
    width: 30%;
  }
}
.rule {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 344px;
  max-height: 664px;
  overflow-y: scroll;
  &__card {
    :deep(.el-input) {
      width: 480px;
      margin-bottom: 16px;
    }
    i {
      cursor: pointer;
    }
    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
      color: #323233;
      &-fold {
        font-size: 20px;
        color: #969799;
      }
      &-example {
        margin-left: 4px;
        font-size: 16px;
        &:hover {
          color: #7a52f4;
        }
      }
      > span {
        display: flex;
        align-items: center;
        &::before {
          display: inline-block;
          flex-shrink: 0;
          width: 4px;
          height: 16px;
          margin-right: 8px;
          content: '';
          background: #7a52f4;
        }
      }
    }

    &-label {
      margin-bottom: 8px;
      font-size: 14px;
      color: #323233;
      span {
        font-size: 14px;
        color: #f54e4e;
      }
    }
    &-permission {
      display: flex;
      width: 480px;
      overflow: hidden;
      border: 1px solid #e5e6eb;
      border-radius: 4px;
      &-text {
        :deep(.el-tag) {
          margin-left: 6px;
        }
        box-sizing: border-box;
        display: flex;
        flex: auto;
        align-items: center;
        height: 32px;
        padding-right: 15px;
        padding-left: 9px;
        overflow-x: auto;
        line-height: 30px;
        cursor: pointer;
      }
      &-btn {
        display: flex;
        align-items: center;
        margin: 0 12px 0 8px;
      }
    }
    &-rule {
      :deep(.el-input) {
        width: 240px;
        margin-bottom: 0;
      }
      display: flex;
      flex-direction: column;
      justify-content: center;
      border: 1px solid #e5e6eb;
      border-radius: 8px;
      &.is-free {
        border: none;
        :deep(.el-input) {
          width: 100%;
          margin-bottom: 0;
        }
      }
      &-polish {
        margin-top: 12px;
      }
      &-item {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        &-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #969799;
        }
        &-level1 {
          display: flex;
          align-items: center;
          i {
            margin-right: 8px;
          }
        }
        &-level2 {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px;
          margin-left: 24px;
          background: #f7f8fa;
          border-radius: 8px;
          &-title {
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: #969799;
          }
        }
        &-add {
          margin-left: 24px;
          cursor: pointer;
        }
      }
      &-divider {
        height: 1px;
        margin: 0 12px;
        background: #e5e6eb;
      }
      &-add {
        padding: 12px;
        cursor: pointer;
      }
    }
    &-checkbox {
      display: flex;
      flex-direction: column;
      &-title {
        font-weight: 600;
        color: #323233;
      }
      &-desc {
        margin-left: 24px;
        font-size: 13px;
        color: #969799;
      }
    }
  }
}
</style>
